import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Building2, Check, ChevronsUpDown, Eye, ExternalLink, X } from 'lucide-react';
import {
    createNotification,
    CreateNotificationPayload,
    NotificationAction,
} from '../../services/api/notificationService';
import { getAllDepartments, getAllUsers } from '../../services/apiService';
import { DocumentRichText } from '../documents/form/inputs/DocumentRichText';
import FileSelector from '../files/FileSelector';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../ui/card';
import {
    Form,
    FormField,
    FormItem,
    FormLabel,
    FormControl,
    FormMessage,
} from '../ui/form';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useToast } from '../../hooks/use-toast';
import { cn } from '../../lib/utils';
import RIGHTS_KEYS from '../../constants/rights-keys';
import FEATURE_KEYS from '../../constants/feature-keys';
import PERMISSION_KEYS from '../../constants/permission-keys';
import { Badge } from '../ui/badge';
import NotificationPreviewDialog from './NotificationPreviewDialog';
import { INotification } from '../../services/api/notificationService';

const FEATURE_VALUES: string[] = Object.values(FEATURE_KEYS);

const PERMISSION_OPTIONS = Object.values(PERMISSION_KEYS).map((value) => ({
    value: value as string,
    label: value as string,
}));

const RIGHTS_OPTIONS = Object.entries(RIGHTS_KEYS).map(([key, value]) => ({
    key,
    value,
    isFeature: FEATURE_VALUES.includes(value),
    label: key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (s) => s.toUpperCase())
        .trim(),
}));

interface SimpleDepartment {
    _id: string;
    name: string;
}

interface SimpleUser {
    id: string;
    name: string;
    email: string;
    role: string;
}

interface FormValues {
    title: string;
    description: string;
    isImportant: boolean;
    targetRights: string[];
    targetPermissions: string[];
    targetDepartmentIds: string[];
    targetUserIds: string[];
    attachedFile: string[];
    actionEnabled: boolean;
    actionLabel: string;
    actionUrl: string;
}

interface NotificationCreateFormProps {
    onCreated: () => void;
}

const NotificationCreateForm: React.FC<NotificationCreateFormProps> = ({
    onCreated,
}) => {
    const { toast } = useToast();
    const [isSaving, setIsSaving] = useState(false);
    const [selectedFileIds, setSelectedFileIds] = useState<string[]>([]);
    const [fileSelectorKey, setFileSelectorKey] = useState(0);
    const [previewOpen, setPreviewOpen] = useState(false);

    // Department picker state
    const [allDepartments, setAllDepartments] = useState<SimpleDepartment[]>([]);
    const [selectedDepartments, setSelectedDepartments] = useState<SimpleDepartment[]>([]);
    const [deptComboboxOpen, setDeptComboboxOpen] = useState(false);
    const [deptSearch, setDeptSearch] = useState('');

    // User picker state
    const [allUsers, setAllUsers] = useState<SimpleUser[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<SimpleUser[]>([]);
    const [comboboxOpen, setComboboxOpen] = useState(false);
    const [userSearch, setUserSearch] = useState('');

    useEffect(() => {
        getAllUsers()
            .then((users: any[]) => {
                setAllUsers(
                    users.map((u) => ({
                        id: u.id,
                        name: u.name || '',
                        email: u.email || '',
                        role: u.role || '',
                    }))
                );
            })
            .catch(() => setAllUsers([]));
        getAllDepartments()
            .then((depts: any[]) => {
                setAllDepartments(
                    depts.map((d: any) => ({ _id: d._id, name: d.name || '' }))
                );
            })
            .catch(() => setAllDepartments([]));
    }, []);

    const form = useForm<FormValues>({
        defaultValues: {
            title: '',
            description: '',
            isImportant: false,
            targetRights: [],
            targetPermissions: [],
            targetDepartmentIds: [],
            targetUserIds: [],
            attachedFile: [],
            actionEnabled: false,
            actionLabel: '',
            actionUrl: '',
        },
    });

    const actionEnabled = form.watch('actionEnabled');

    const buildPreviewNotification = (): INotification => {
        const data = form.getValues();
        return {
            title: data.title || 'Untitled',
            description: data.description || '',
            isImportant: data.isImportant,
            video_id: selectedFileIds[0],
            targetRights: data.targetRights,
            targetPermissions: data.targetPermissions,
            targetDepartmentIds: data.targetDepartmentIds,
            action:
                data.actionEnabled && data.actionLabel && data.actionUrl
                    ? {
                          type: 'external_link',
                          label: data.actionLabel,
                          url: data.actionUrl,
                      }
                    : undefined,
        };
    };

    const handleFilesSelected = (fileIds: string[]) => {
        setSelectedFileIds(fileIds);
        form.setValue('attachedFile', fileIds);
    };

    const addUser = (user: SimpleUser) => {
        if (selectedUsers.some((u) => u.id === user.id)) return;
        const next = [...selectedUsers, user];
        setSelectedUsers(next);
        form.setValue(
            'targetUserIds',
            next.map((u) => u.id)
        );
        form.clearErrors('targetUserIds');
        setUserSearch('');
        setComboboxOpen(false);
    };

    const removeUser = (userId: string) => {
        const next = selectedUsers.filter((u) => u.id !== userId);
        setSelectedUsers(next);
        form.setValue(
            'targetUserIds',
            next.map((u) => u.id)
        );
    };

    const addDepartment = (dept: SimpleDepartment) => {
        if (selectedDepartments.some((d) => d._id === dept._id)) return;
        const next = [...selectedDepartments, dept];
        setSelectedDepartments(next);
        form.setValue(
            'targetDepartmentIds',
            next.map((d) => d._id)
        );
        form.clearErrors('targetRights');
        setDeptSearch('');
        setDeptComboboxOpen(false);
    };

    const removeDepartment = (deptId: string) => {
        const next = selectedDepartments.filter((d) => d._id !== deptId);
        setSelectedDepartments(next);
        form.setValue(
            'targetDepartmentIds',
            next.map((d) => d._id)
        );
    };

    const filteredDepartments = allDepartments.filter((d) => {
        if (selectedDepartments.some((s) => s._id === d._id)) return false;
        if (!deptSearch) return true;
        return d.name.toLowerCase().includes(deptSearch.toLowerCase());
    });

    const filteredUsers = allUsers.filter((u) => {
        if (selectedUsers.some((s) => s.id === u.id)) return false;
        if (!userSearch) return true;
        const q = userSearch.toLowerCase();
        return (
            u.name.toLowerCase().includes(q) ||
            u.email.toLowerCase().includes(q)
        );
    });

    const resetForm = () => {
        form.reset();
        setSelectedFileIds([]);
        setSelectedDepartments([]);
        setSelectedUsers([]);
        setFileSelectorKey((prev) => prev + 1);
    };

    const onSubmit = async (data: FormValues) => {
        const hasRights = data.targetRights.length > 0;
        const hasPermissions = data.targetPermissions.length > 0;
        const hasDepartments = selectedDepartments.length > 0;
        const hasUsers = selectedUsers.length > 0;

        if (!hasRights && !hasPermissions && !hasDepartments && !hasUsers) {
            form.setError('targetRights', {
                message:
                    'Select at least one right, permission, department, or user as the audience.',
            });
            return;
        }

        setIsSaving(true);
        try {
            const payload: CreateNotificationPayload = {
                title: data.title.trim(),
                description: data.description.trim(),
                isImportant: data.isImportant,
            };
            if (data.targetRights.length > 0) {
                payload.targetRights = data.targetRights;
            }
            if (data.targetPermissions.length > 0) {
                payload.targetPermissions = data.targetPermissions;
            }
            if (selectedDepartments.length > 0) {
                payload.targetDepartmentIds = selectedDepartments.map((d) => d._id);
            }
            if (selectedUsers.length > 0) {
                payload.targetUserIds = selectedUsers.map((u) => u.id);
            }
            if (selectedFileIds.length > 0) {
                payload.video_id = selectedFileIds[0];
            }
            if (data.actionEnabled && data.actionLabel.trim() && data.actionUrl.trim()) {
                payload.action = {
                    type: 'external_link',
                    label: data.actionLabel.trim(),
                    url: data.actionUrl.trim(),
                };
            }
            await createNotification(payload);
            toast({ title: 'Notification created successfully.' });
            resetForm();
            onCreated();
        } catch (err: any) {
            toast({
                title: 'Failed to create notification.',
                description:
                    err.response?.data?.message ||
                    err.response?.data?.error ||
                    undefined,
                variant: 'destructive',
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-6"
            >
                {/* Content */}
                <Card className="border-white/10">
                    <CardHeader>
                        <CardTitle className="text-base font-medium">
                            Content
                        </CardTitle>
                        <CardDescription>
                            Title and message for the notification.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="grid gap-5">
                        <FormField
                            control={form.control}
                            name="title"
                            rules={{ required: 'Title is required' }}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-xs" required>
                                        Title
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            placeholder="Notification title"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="description"
                            rules={{ required: 'Message is required' }}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="text-xs" required>
                                        Message
                                    </FormLabel>
                                    <FormControl>
                                        <DocumentRichText
                                            value={field.value}
                                            onChange={field.onChange}
                                            placeholder="Write your notification message…"
                                            className="max-h-[220px] flex flex-col [&_.ProseMirror]:min-h-[120px] [&_.ProseMirror]:max-h-[170px] [&_.ProseMirror]:overflow-y-auto"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="isImportant"
                            render={({ field }) => (
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                    <FormControl>
                                        <Checkbox
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                        />
                                    </FormControl>
                                    <FormLabel className="text-xs cursor-pointer">
                                        Mark as important (auto-shows to users)
                                    </FormLabel>
                                </FormItem>
                            )}
                        />
                    </CardContent>
                </Card>

                {/* Audience */}
                <Card className="border-white/10">
                    <CardHeader>
                        <CardTitle className="text-base font-medium">
                            Audience
                        </CardTitle>
                        <CardDescription>
                            Choose who receives this notification. Duplicates
                            are handled automatically.
                        </CardDescription>
                        <ul className="mt-2 space-y-1 text-xs text-muted-foreground list-disc pl-4">
                            <li>
                                <span className="font-medium text-foreground">
                                    Rights:
                                </span>{' '}
                                a user matches if they have any selected right.
                                Rights marked{' '}
                                <span className="font-medium">Feature</span>{' '}
                                also require that right to be enabled on the
                                user's department; other rights only check the
                                user's role.
                            </li>
                            <li>
                                <span className="font-medium text-foreground">
                                    Permissions:
                                </span>{' '}
                                checked on the individual user. Rights and
                                permissions are combined with{' '}
                                <span className="font-medium">OR</span> — a user
                                matches with any selected right or permission.
                            </li>
                            <li>
                                <span className="font-medium text-foreground">
                                    Departments:
                                </span>{' '}
                                combined with rights and permissions using{' '}
                                <span className="font-medium">AND</span> — when
                                both are set, users must be in a selected
                                department <em>and</em> match a right or
                                permission. On their own, all users in the
                                selected departments are targeted.
                            </li>
                            <li>
                                <span className="font-medium text-foreground">
                                    Individual users:
                                </span>{' '}
                                always included, independent of rights,
                                permissions, and departments.
                            </li>
                        </ul>
                    </CardHeader>
                    <CardContent className="grid gap-5">
                        {/* Rights & permissions */}
                        <div className="space-y-4">
                            <FormLabel className="text-xs">
                                Target rights &amp; permissions
                            </FormLabel>

                            {/* Rights subsection */}
                            <FormField
                                control={form.control}
                                name="targetRights"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                            Rights
                                        </FormLabel>
                                        <p className="text-[11px] text-muted-foreground">
                                            Checked on the user's role. Items
                                            marked{' '}
                                            <span className="font-medium">
                                                Feature
                                            </span>{' '}
                                            also require the right on the user's
                                            department.
                                        </p>
                                        <div className="h-40 overflow-auto rounded-md border p-3 mt-1">
                                            <div className="grid gap-2 sm:grid-cols-2">
                                                {RIGHTS_OPTIONS.map(
                                                    ({
                                                        key,
                                                        value,
                                                        label,
                                                        isFeature,
                                                    }) => {
                                                        const checked = (
                                                            field.value || []
                                                        ).includes(value);
                                                        return (
                                                            <div
                                                                key={key}
                                                                className="flex items-center space-x-2"
                                                            >
                                                                <Checkbox
                                                                    id={`right-${key}`}
                                                                    checked={
                                                                        checked
                                                                    }
                                                                    onCheckedChange={() => {
                                                                        const next =
                                                                            checked
                                                                                ? field.value.filter(
                                                                                      (
                                                                                          r: string
                                                                                      ) =>
                                                                                          r !==
                                                                                          value
                                                                                  )
                                                                                : [
                                                                                      ...field.value,
                                                                                      value,
                                                                                  ];
                                                                        field.onChange(
                                                                            next
                                                                        );
                                                                        form.clearErrors(
                                                                            'targetRights'
                                                                        );
                                                                    }}
                                                                />
                                                                <label
                                                                    htmlFor={`right-${key}`}
                                                                    className="flex items-center gap-1.5 text-xs font-medium leading-none cursor-pointer"
                                                                >
                                                                    {label}
                                                                    {isFeature && (
                                                                        <Badge
                                                                            variant="outline"
                                                                            className="px-1 py-0 text-[9px] font-normal"
                                                                        >
                                                                            Feature
                                                                        </Badge>
                                                                    )}
                                                                </label>
                                                            </div>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {/* Permissions subsection */}
                            <FormField
                                control={form.control}
                                name="targetPermissions"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                                            Permissions
                                        </FormLabel>
                                        <p className="text-[11px] text-muted-foreground">
                                            Checked on the individual user's
                                            department permissions.
                                        </p>
                                        <div className="rounded-md border p-3 mt-1">
                                            <div className="grid gap-2 sm:grid-cols-2">
                                                {PERMISSION_OPTIONS.map(
                                                    ({ value, label }) => {
                                                        const checked = (
                                                            field.value || []
                                                        ).includes(value);
                                                        return (
                                                            <div
                                                                key={value}
                                                                className="flex items-center space-x-2"
                                                            >
                                                                <Checkbox
                                                                    id={`perm-${value}`}
                                                                    checked={
                                                                        checked
                                                                    }
                                                                    onCheckedChange={() => {
                                                                        const next =
                                                                            checked
                                                                                ? field.value.filter(
                                                                                      (
                                                                                          p: string
                                                                                      ) =>
                                                                                          p !==
                                                                                          value
                                                                                  )
                                                                                : [
                                                                                      ...field.value,
                                                                                      value,
                                                                                  ];
                                                                        field.onChange(
                                                                            next
                                                                        );
                                                                        form.clearErrors(
                                                                            'targetRights'
                                                                        );
                                                                    }}
                                                                />
                                                                <label
                                                                    htmlFor={`perm-${value}`}
                                                                    className="text-xs font-medium leading-none cursor-pointer"
                                                                >
                                                                    {label}
                                                                </label>
                                                            </div>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        </div>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>

                        {/* Departments */}
                        <FormField
                            control={form.control}
                            name="targetDepartmentIds"
                            render={() => (
                                <FormItem>
                                    <FormLabel className="text-xs">
                                        <span className="inline-flex items-center gap-1.5">
                                            <Building2 className="h-3.5 w-3.5" />
                                            Target departments
                                        </span>
                                    </FormLabel>
                                    <p className="text-[11px] text-muted-foreground mb-1">
                                        AND'd with rights when both are set —
                                        recipients must be in a selected
                                        department and match the rights.
                                    </p>

                                    <Popover
                                        open={deptComboboxOpen}
                                        onOpenChange={setDeptComboboxOpen}
                                    >
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                role="combobox"
                                                aria-expanded={deptComboboxOpen}
                                                className="w-full justify-between font-normal text-muted-foreground"
                                            >
                                                Search departments…
                                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                                            <Command shouldFilter={false}>
                                                <CommandInput
                                                    placeholder="Search by name…"
                                                    value={deptSearch}
                                                    onValueChange={setDeptSearch}
                                                />
                                                <CommandList>
                                                    <CommandEmpty>
                                                        No departments found.
                                                    </CommandEmpty>
                                                    <CommandGroup>
                                                        {filteredDepartments
                                                            .slice(0, 20)
                                                            .map((dept) => (
                                                                <CommandItem
                                                                    key={dept._id}
                                                                    value={dept._id}
                                                                    onSelect={() =>
                                                                        addDepartment(dept)
                                                                    }
                                                                >
                                                                    <Check
                                                                        className={cn(
                                                                            'mr-2 h-4 w-4',
                                                                            selectedDepartments.some(
                                                                                (s) =>
                                                                                    s._id === dept._id
                                                                            )
                                                                                ? 'opacity-100'
                                                                                : 'opacity-0'
                                                                        )}
                                                                    />
                                                                    <span className="text-sm">
                                                                        {dept.name}
                                                                    </span>
                                                                </CommandItem>
                                                            ))}
                                                    </CommandGroup>
                                                </CommandList>
                                            </Command>
                                        </PopoverContent>
                                    </Popover>

                                    {selectedDepartments.length > 0 && (
                                        <div className="mt-2 space-y-1">
                                            {selectedDepartments.map((dept) => (
                                                <div
                                                    key={dept._id}
                                                    className="flex items-center justify-between rounded-md border px-3 py-1.5"
                                                >
                                                    <p className="text-xs font-medium truncate">
                                                        {dept.name}
                                                    </p>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                                                        onClick={() =>
                                                            removeDepartment(dept._id)
                                                        }
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Individual users */}
                        <FormField
                            control={form.control}
                            name="targetUserIds"
                            render={() => (
                                <FormItem>
                                    <FormLabel className="text-xs">
                                        Individual users
                                    </FormLabel>
                                    <p className="text-[11px] text-muted-foreground">
                                        Always included, independent of rights
                                        and departments.
                                    </p>

                                    {/* Combobox */}
                                    <Popover
                                        open={comboboxOpen}
                                        onOpenChange={setComboboxOpen}
                                    >
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                role="combobox"
                                                aria-expanded={comboboxOpen}
                                                className="w-full justify-between font-normal text-muted-foreground"
                                            >
                                                Search users…
                                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                                            <Command shouldFilter={false}>
                                                <CommandInput
                                                    placeholder="Search by name or email…"
                                                    value={userSearch}
                                                    onValueChange={
                                                        setUserSearch
                                                    }
                                                />
                                                <CommandList>
                                                    <CommandEmpty>
                                                        No users found.
                                                    </CommandEmpty>
                                                    <CommandGroup>
                                                        {filteredUsers
                                                            .slice(0, 20)
                                                            .map((user) => (
                                                                <CommandItem
                                                                    key={
                                                                        user.id
                                                                    }
                                                                    value={
                                                                        user.id
                                                                    }
                                                                    onSelect={() =>
                                                                        addUser(
                                                                            user
                                                                        )
                                                                    }
                                                                >
                                                                    <Check
                                                                        className={cn(
                                                                            'mr-2 h-4 w-4',
                                                                            selectedUsers.some(
                                                                                (
                                                                                    s
                                                                                ) =>
                                                                                    s.id ===
                                                                                    user.id
                                                                            )
                                                                                ? 'opacity-100'
                                                                                : 'opacity-0'
                                                                        )}
                                                                    />
                                                                    <div className="flex flex-col">
                                                                        <span className="text-sm">
                                                                            {user.name ||
                                                                                user.email}
                                                                        </span>
                                                                        {user.name && (
                                                                            <span className="text-[10px] text-muted-foreground">
                                                                                {
                                                                                    user.email
                                                                                }
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </CommandItem>
                                                            ))}
                                                    </CommandGroup>
                                                </CommandList>
                                            </Command>
                                        </PopoverContent>
                                    </Popover>

                                    {/* Selected users list */}
                                    {selectedUsers.length > 0 && (
                                        <div className="mt-2 space-y-1">
                                            {selectedUsers.map((user) => (
                                                <div
                                                    key={user.id}
                                                    className="flex items-center justify-between rounded-md border px-3 py-1.5"
                                                >
                                                    <div className="min-w-0">
                                                        <p className="text-xs font-medium truncate">
                                                            {user.name ||
                                                                user.email}
                                                        </p>
                                                        {user.name && (
                                                            <p className="text-[10px] text-muted-foreground truncate">
                                                                {user.email}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-6 w-6 shrink-0 text-muted-foreground hover:text-destructive"
                                                        onClick={() =>
                                                            removeUser(user.id)
                                                        }
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </CardContent>
                </Card>

                {/* Attachment */}
                <Card className="border-white/10">
                    <CardHeader>
                        <CardTitle className="text-base font-medium">
                            Attachment
                        </CardTitle>
                        <CardDescription>
                            Optionally attach a video or image.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <FileSelector
                            key={fileSelectorKey}
                            onFilesSelected={handleFilesSelected}
                            allowedTypes={{
                                video: true,
                                image: true,
                                document: false,
                            }}
                            allowMultiple={false}
                            label="Select video or image"
                            ownerType="nordinsight"
                        />
                    </CardContent>
                </Card>

                {/* Action (External Link) */}
                <Card className="border-white/10">
                    <CardHeader>
                        <CardTitle className="text-base font-medium">
                            Action
                        </CardTitle>
                        <CardDescription>
                            Optionally attach an action button to this
                            notification.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="grid gap-5">
                        <FormField
                            control={form.control}
                            name="actionEnabled"
                            render={({ field }) => (
                                <FormItem className="flex items-center space-x-2 space-y-0">
                                    <FormControl>
                                        <Checkbox
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                        />
                                    </FormControl>
                                    <FormLabel className="text-xs cursor-pointer">
                                        <span className="inline-flex items-center gap-1.5">
                                            <ExternalLink className="h-3.5 w-3.5" />
                                            Include external link button
                                        </span>
                                    </FormLabel>
                                </FormItem>
                            )}
                        />

                        {actionEnabled && (
                            <div className="grid gap-4 sm:grid-cols-2">
                                <FormField
                                    control={form.control}
                                    name="actionLabel"
                                    rules={
                                        actionEnabled
                                            ? {
                                                  required:
                                                      'Button label is required',
                                              }
                                            : undefined
                                    }
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel
                                                className="text-xs"
                                                required
                                            >
                                                Button label
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    placeholder="e.g. Open documentation"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="actionUrl"
                                    rules={
                                        actionEnabled
                                            ? {
                                                  required: 'URL is required',
                                                  pattern: {
                                                      value: /^https?:\/\/.+/,
                                                      message:
                                                          'Must be a valid URL starting with http:// or https://',
                                                  },
                                              }
                                            : undefined
                                    }
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel
                                                className="text-xs"
                                                required
                                            >
                                                URL
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    placeholder="https://example.com"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Form Actions */}
                <div className="flex items-center justify-end gap-3 pt-2 pb-8">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={resetForm}
                    >
                        Reset
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        className="gap-1.5"
                        onClick={() => setPreviewOpen(true)}
                    >
                        <Eye className="h-4 w-4" />
                        Preview
                    </Button>
                    <Button type="submit" disabled={isSaving}>
                        {isSaving ? 'Creating…' : 'Create notification'}
                    </Button>
                </div>

                <NotificationPreviewDialog
                    notification={previewOpen ? buildPreviewNotification() : null}
                    open={previewOpen}
                    onOpenChange={setPreviewOpen}
                />
            </form>
        </Form>
    );
};

export default NotificationCreateForm;
